import { type RowData, type Table } from "@tanstack/react-table";
import cx from "classnames";
import { type ForwardedRef, forwardRef } from "react";

import { DataGridColumnHeader } from "../DataGridColumnHeader";
import { type DataGridColumnDef } from "../useColumnState";
import { GroupedRows } from "./GroupedRows";
import { type TableGroup, getTableHeaderGroups } from "./TableGroup";
import { UngroupedRows } from "./UngroupedRows";
import { useInferredColumnTypes } from "./useInferredColumnTypes";

type Props<T extends RowData> = {
	table: Table<T>;
	tableGroup?: TableGroup;
	tableVariant: "default" | "striped";
	rowSelectBehavior?: "none" | "select" | "multiselect";
	rowSelectTrigger?: "onClickRow" | "onClickToggle";
	grouped: boolean;
};

function BaseCustomTable<T extends RowData>(
	{ table, tableGroup, tableVariant, rowSelectBehavior, rowSelectTrigger, grouped }: Props<T>,
	ref: ForwardedRef<HTMLDivElement>
) {
	const [headerGroups] = getTableHeaderGroups(table, tableGroup);

	const allColumns = table.getAllLeafColumns();
	const inferredColumnTypes = useInferredColumnTypes<T>(table.getRowModel().rows[0], allColumns);

	return (
		<div ref={ref} className={"text-sm transition-all"}>
			<div
				className={cx(
					// IMPORTANT: z-index MUST go here to prevent headings from disappearing behind
					// buttons inside table rows. The reason is that z-index requires the
					// element to be positioned, and the header groups are not.
					"z-surface",
					"sticky top-0"
				)}
			>
				{headerGroups.map((headerGroup) => (
					<tr
						key={headerGroup.id}
						className={cx(
							// "[&>:not(:last-child)]:border-r",
							// "[&>:last-child]:shadow-border-right",
							"shadow-border-bottom flex flex-row overflow-x-hidden group-focus/compute-cell:rounded-t-md group-[.prosemirror-compute-cell--selected]:rounded-t-md group-focus/compute-cell:[&>:first-child]:rounded-tl-md group-[.prosemirror-compute-cell--selected]:[&>:first-child]:rounded-tl-md group-focus/compute-cell:[&>:last-child]:rounded-tr-md group-[.prosemirror-compute-cell--selected]:[&>:last-child]:rounded-tr-md",
							{ shadow: grouped }
						)}
					>
						{headerGroup.headers.map((header, i) => (
							<DataGridColumnHeader
								key={header.id}
								headerID={header.id}
								table={table}
								header={header}
								columnDef={
									header.column.columnDef as DataGridColumnDef<unknown, unknown>
								}
								inferredFieldType={inferredColumnTypes[header.id]}
								isFirstCol={i === 0}
								isLastCol={i === headerGroup.headers.length - 1}
								isGrouped={grouped}
								headerIndex={i}
								rowSelectBehavior={rowSelectBehavior}
							/>
						))}
					</tr>
				))}
			</div>
			<div
				className={cx(
					!grouped &&
						cx(
							{
								// NOTE: (even) rather than (odd) is bg-secondary because it contrasts
								// with the bg-tertiary in the table column headers.
								"[&>*:nth-child(even)]:bg-secondary": tableVariant === "striped",
							},
							"[&>*]:shadow-border-bottom",
							{
								"hover:[&>*]:bg-tertiary":
									(rowSelectBehavior === "select" ||
										rowSelectBehavior === "multiselect") &&
									rowSelectTrigger !== "onClickToggle",
							}
						)
				)}
			>
				{!grouped ? (
					<UngroupedRows
						table={table}
						tableGroup={tableGroup}
						rowSelectBehavior={rowSelectBehavior}
						rowSelectTrigger={rowSelectTrigger}
					/>
				) : (
					<GroupedRows
						table={table}
						tableGroup={tableGroup}
						rowSelectBehavior={rowSelectBehavior}
						rowSelectTrigger={rowSelectTrigger}
					/>
				)}
			</div>
		</div>
	);
}

export const CustomTable = forwardRef(BaseCustomTable);
